<template>
  <!-- Bubble -->
  <Teleport to="#app">
    <transition>
      <button
        v-show="! coursesInview"
        class="a-bubble"
        type="button"
        @click.prevent="scrollToCourses"
      >
        <span
          class="a-bubble__text"
          v-text="total
            ? total === 1 ? '1 ' + $t('courses_overview.result') : total + ' ' + $t('courses_overview.results')
            : $t('courses_overview.no_results')
          ">
        </span>
        <svg class="a-bubble__icon" aria-hidden="true">
          <use :xlink:href="$root.spritePath + '#btn-arrow-up'" />
        </svg>
      </button>
    </transition>
  </Teleport>

  <!-- Mobile filters -->
  <Teleport to="#app">
    <transition>
      <div ref="mobileFilters" v-if="modalActive" class="m-filters-mobile">
        <div class="m-filters-mobile__inner">
          <div class="m-filters-mobile__header">
            <h2 class="a-h2 m-filters-mobile__title">{{ $t('courses_overview.filter') }}</h2>

            <button
              class="m-filters-mobile__reset"
              type="button"
              @click.prevent=";[filterDepartment = null, resetAndLoad()]"
            >
              {{ $t('courses_overview.reset_filters') }}
            </button>
          </div>
          <div class="m-filters-mobile__main">
            <!-- Filters - Mobile -->
            <div class="m-filters">
              <BaseCoursesFilter
                v-if="filters"
                :filters="filters"
                v-model:filterDepartment.number="filterDepartment"
                v-model:filterDisciplines="filterDisciplines"
                v-model:filterAges="filterAges"
                v-model:filterDay.number="filterDay"
                v-model:filterMoments="filterMoments"
                v-model:filterLanguages="filterLanguages"
                @update:filterDepartment="resetAndLoad"
                @update:filterDisciplines=";[setPage(1), load()]"
                @update:filterAges=";[setPage(1), load()]"
                @update:filterDay=";[setPage(1), (filterMoments = []), load()]"
                @update:filterMoments=";[setPage(1), load()]"
                @update:filterLanguages=";[setPage(1), load()]"
              ></BaseCoursesFilter>
            </div>
          </div>
          <div class="m-filters-mobile__footer">
            <button
              type="button"
              @click.prevent="toggleFilters"
              class="a-btn-pill"
              v-text="$t('filters.close')"
            ></button>
          </div>
        </div>
        <button
          type="button"
          @click.prevent="toggleFilters"
          class="m-filters-mobile__backdrop"
        ></button>
      </div>
    </transition>
  </Teleport>

  <!-- Overview -->
  <div class="o-layout o-overview">
    <div class="o-overview__mobile-header u-col-start:1 u-col-end:13">
      <button
        type="button"
        @click.prevent="toggleFilters"
        class="a-btn-pill"
      >
        {{ $t('courses_overview.filter') }}
      </button>

      <div class="m-filter-search">
        <label
          for="search-courses-mobile"
          class="u-screen-reader-only"
          v-text="$t('courses_overview.search')"
        ></label>
        <input
          class="m-filter-search__input"
          type="text"
          id="search-courses-mobile"
          :placeholder="$t('courses_overview.search')"
          v-model="search"
          v-debounce:500ms="resetAndLoad"
        />

        <button class="m-filter-search__btn" type="button" @click="load">
          <svg aria-hidden="true">
            <use :xlink:href="$root.spritePath + '#search'" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Sidebar -->
    <!-- Filters - Desktop -->
    <aside
      v-if="!modalActive"
      class="m-filters u-col-start:1@lg u-col-end:4@lg u-hidden@md-down"
    >
      <a class="u-skip-link" href="#courses-overview">{{ $t('skip_filters') }}</a>
      <nav class="m-filters__group" aria-labelledby="search-label">
        <h2 class="a-h3" id="search-label">{{ $t('courses_overview.find_your_course') }}</h2>
        <div class="m-filter-search">
          <label
            for="search-courses"
            class="u-screen-reader-only"
            v-text="$t('courses_overview.search')"
          ></label>
          <input
            class="m-filter-search__input"
            type="text"
            id="search-courses"
            :placeholder="searchPlaceholder"
            v-model="search"
            v-debounce:500ms="resetAndLoad"
            aria-labelledby="search-label"
          />
          <button class="m-filter-search__btn" type="button" :title="$t('courses_overview.search')" @click="load">
            <svg aria-hidden="true">
              <use :xlink:href="$root.spritePath + '#search'" />
            </svg>
          </button>
        </div>
      </nav>

      <BaseCoursesFilter
        v-if="filters"
        :filters="filters"
        v-model:filterDepartment.number="filterDepartment"
        v-model:filterDisciplines="filterDisciplines"
        v-model:filterAges="filterAges"
        v-model:filterDay.number="filterDay"
        v-model:filterMoments="filterMoments"
        v-model:filterLanguages="filterLanguages"
        @update:filterDepartment="resetAndLoad"
        @update:filterDisciplines=";[setPage(1), load()]"
        @update:filterAges=";[setPage(1), load()]"
        @update:filterDay=";[setPage(1), (filterMoments = []), load()]"
        @update:filterMoments=";[setPage(1), load()]"
        @update:filterLanguages=";[setPage(1), load()]"
        aria-labelledby="courses-filters-label"
      >
        <div class="m-filters__heading">
          <h2
            id="courses-filters-label"
            class="a-h3 u-mt-0 u-mb-0"
          >
            {{ $t('courses_overview.filters') }}
          </h2>

          <button
            class="m-filters__reset"
            type="button"
            @click.prevent=";[filterDepartment = null, resetAndLoad()]"
          >
            {{ $t('courses_overview.reset_filters') }}
          </button>
        </div>
      </BaseCoursesFilter>
    </aside>

    <!-- Main -->
    <section
      class="o-overview__main u-col-start:5@lg u-col-end:13@lg"
      aria-labelledby="courses-title"
    >
      <header class="o-overview__main-header">
        <h2
          id="courses-title"
          class="u-screen-reader-only"
          v-text="$t('courses_overview.courses')"
        ></h2>
        <span class="o-overview__results-count a-h4">{{ total }} {{ total !== 1 ? $t('courses_overview.results') : $t('courses_overview.result') }}</span>

        <!--
        BVRDG-108 sorting not implemented for now. Keep slice in
        the codebase so we won't forget it existed.

        <select class="a-select">
          <option value="0">Standaard</option>
          <option value="1">Startdatum (oplopend)</option>
          <option value="2">Startdatum (aflopend)</option>
        </select>
        -->
      </header>

      <main
        v-element-visibility="onElementVisibility"
        class="o-overview__main-content o-layout"
        id="courses-overview"
      >
        <!-- Loading -->
        <template v-if="loading">
          <div class="u-col-span:6@md" v-for="(item, index) in Array(12)" :key="index">
            <div class="m-tile m-tile--loading"></div>
          </div>
        </template>

        <!-- Error after loading -->
        <template v-else-if="loadingError">
          <div class="u-col-span:12 m-paragraph o-editor-content">
            <p>
              {{ $t('courses_overview.loading_error') }}
            </p>
          </div>
        </template>

        <!-- No results -->
        <template v-else-if="items.length === 0">
          <div class="u-col-span:12 m-paragraph o-editor-content">
            <p>
              {{ $t('courses_overview.no_content') }}
            </p>
          </div>
        </template>

        <!-- Items -->
        <template v-else>
          <template v-for="(entry, index) in items" :key="entry.id">
            <slot name="alert" v-if="index === 8"></slot>

            <!-- Tile -->
            <div class="u-col-span:6@md">
              <a :href="entry.url" class="m-tile">
                <ul class="m-tile__tags">
                  <li v-for="label in entry.labels" :key="label.title">
                    <div
                      class="m-tile__tag a-tag"
                      v-text="label.type == 'age'
                        ? label.title + $t('tile.yr')
                        : label.title
                      "
                    ></div>
                  </li>
                </ul>

                <!-- Hide these labels when there are three or more tags: BVRDG-519 -->
                <template v-if="entry.labels.length < 3">
                  <ul v-if="entry.is_tip || entry.is_new" class="m-tile__tags m-tile__tags--right">
                    <li>
                      <div class="m-tile__tag a-tag a-tag--highlighted">
                        {{ entry.is_new ? $t('tile.new') : $t('tile.hint') }}
                      </div>
                    </li>
                  </ul>
                </template>

                <picture v-if="entry.image" class="a-image m-tile__image">
                  <source :data-srcset="entry.image.srcset" />
                  <img
                    class="a-image__img"
                    :data-src="entry.image.src"
                    :alt="entry.image.alt"
                    loading="lazy"
                  />
                </picture>
                <div class="m-tile__header">
                  <svg class="m-tile__arrow" aria-hidden="true">
                    <use :xlink:href="$root.spritePath + '#btn-arrow-right'" />
                  </svg>
                  <h3 class="a-h3 m-tile__title" v-text="entry.title"></h3>
                </div>
                <ul class="m-tile__main m-tile__details">
                  <li
                    v-if="entry.formatted_date && entry.formatted_date !== ''"
                    class="a-icon-label m-tile__details-item"
                  >
                    <svg class="a-icon-label__icon" :aria-label="$t('courses_overview.day')">
                      <use :xlink:href="$root.spritePath + '#date'" />
                    </svg>
                    <span class="a-icon-label__text">
                      {{ entry.formatted_date }}
                    </span>
                  </li>

                  <li
                    v-if="entry.formatted_day_parts && entry.formatted_day_parts !== ''"
                    class="a-icon-label m-tile__details-item"
                  >
                    <svg class="a-icon-label__icon" :aria-label="$t('courses_overview.daypart')">
                      <use :xlink:href="$root.spritePath + '#clock'" />
                    </svg>
                    <span class="a-icon-label__text">
                      {{ entry.formatted_day_parts }}
                    </span>
                  </li>

                  <li
                    v-if="entry.teachers.length"
                    class="a-icon-label m-tile__details-item m-tile__details-teachers"
                  >
                    <svg class="a-icon-label__icon" :aria-label="$t('courses_overview.teacher')">
                      <use :xlink:href="$root.spritePath + '#person'" />
                    </svg>
                    <span class="a-icon-label__text">
                      {{ formatTeachers(entry.teachers) }}
                    </span>
                  </li>
                </ul>
              </a>
            </div>
          </template>
        </template>
      </main>

      <footer v-if="totalPages > 1" class="o-overview__main-footer">
        <BasePagination
          v-model="filterPage"
          :total="total"
          :total-pages="totalPages"
          @update:modelValue=";[scrollToCourses(), load()]"
        ></BasePagination>
      </footer>
    </section>
  </div>
</template>

<script>
import BaseCoursesFilter from './BaseCoursesFilter.vue'
import BasePagination from './BasePagination.vue'
import allyMaintainDisabled from 'ally.js/maintain/disabled'
import allyWhenKey from 'ally.js/when/key'
import axios from 'axios'
import { vueDebounce } from 'vue-debounce'
import { vElementVisibility } from '@vueuse/components'

export default {
  directives: {
    debounce: vueDebounce({ lock: true }),
    ElementVisibility: vElementVisibility,
  },

  components: {
    BaseCoursesFilter,
    BasePagination,
  },

  props: {
    searchPlaceholder: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    items: [],
    filters: null,
    total: "…",
    totalPages: null,
    coursesInview: true,

    search: "",

    filterPage: 1,
    filterDepartment: null,
    filterDisciplines: [],
    filterAges: [],
    filterDay: null,
    filterMoments: [],
    filterLanguages: [],

    loading: true,
    loadingError: false,
    modalActive: false,
    disabledHandle: null,
    keyHandle: null,
  }),

  async mounted() {
    this.loadParams()
    this.load()
  },

  methods: {
    loadParams() {
      const params = new URLSearchParams(window.location.search)

      if (params.get("q")) {
        this.search = params.get("q")
      }

      if (params.get("page")) {
        this.filterPage = parseInt(params.get("page"), 10)
      }

      if (params.get("department")) {
        this.filterDepartment = parseInt(params.get("department"), 10)
      }

      if (params.get("disciplines")) {
        this.filterDisciplines = params.get("disciplines").split(',').map((value) => value.trim())
      }

      if (params.get("ages")) {
        this.filterAges = params.get("ages").split(',').map((value) => value.trim())
      }

      if (params.get("day")) {
        this.filterDay = parseInt(params.get("day"), 10)
      }

      if (params.get("moments")) {
        this.filterMoments = params.get("moments").split(',').map((value) => value.trim())
      }

      if (params.get("languages")) {
        this.filterLanguages = params.get("languages").split(',').map((value) => value.trim())
      }
    },

    updateParams() {
      const params = new URLSearchParams(window.location.search)

      this.search !== ""
        ? params.set("q", this.search)
        : params.delete("q")

      this.filterPage !== 1
        ? params.set("page", this.filterPage)
        : params.delete("page")

      this.filterDepartment
        ? params.set("department", this.filterDepartment)
        : params.delete("department")

      this.filterDisciplines.length
        ? params.set("disciplines", this.filterDisciplines.join(','))
        : params.delete("disciplines")

      this.filterAges.length
        ? params.set("ages", this.filterAges.join(','))
        : params.delete("ages")

      this.filterDay
        ? params.set("day", this.filterDay)
        : params.delete("day")

      this.filterMoments.length
        ? params.set("moments", this.filterMoments.join(','))
        : params.delete("moments")

      this.filterLanguages.length
        ? params.set("languages", this.filterLanguages.join(','))
        : params.delete("languages")

      // Update state without adding history.
      const newParams = params.toString()

      newParams === ""
        ? history.replaceState({}, null, window.location.pathname)
        : history.replaceState({}, null, window.location.pathname + "?" + newParams)
    },

    resetAndLoad() {
      this.setPage(1)
      this.resetFilters()
      this.load()
    },

    resetFilters() {
      this.filterDisciplines = []
      this.filterAges = []
      this.filterDay = null
      this.filterMoments = []
      this.filterLanguages = []
    },

    setPage(page) {
      // Scroll to top of the page when the page differs.
      if (this.filterPage !== page) {
        this.scrollToCourses()
      }

      this.filterPage = page
    },

    scrollToCourses() {
      const duration = 500,
            bodyRect = document.body.getBoundingClientRect(),
            elemRect = document.body.querySelector('#content').getBoundingClientRect(),
            startingY = window.scrollY,
            offset = elemRect.top - bodyRect.top,
            diff = offset - startingY

      let start

      // Bootstrap our animation - it will get called right before next frame shall be rendered.
      window.requestAnimationFrame(function step(timestamp) {
        if (!start) start = timestamp;
        // Elapsed milliseconds since start of scrolling.
        const time = timestamp - start;
        // Get percent of completion in range [0, 1].
        const percent = Math.min(time / duration, 1);

        window.scrollTo(0, startingY + diff * percent);

        // Proceed with animation as long as we wanted it to.
        if (time < duration) {
          window.requestAnimationFrame(step);
        }
      })
    },

    async load() {

      // Add a small delay to allow scrolling to the top before load.
      setTimeout(async() => {
        // Update the url params.
        this.updateParams()

        // Set the states.
        this.loading = true
        this.loadingError = false

        // Filter for selected disciplines. Filter for the selected department
        // when no disciplines are selected. Clone the array with .slice()
        let disciplineQuery = this.filterDisciplines.slice()
        if (!disciplineQuery.length && this.filterDepartment) {
          disciplineQuery.push(this.filterDepartment)
        }

        // Filter for selected moments. Filter for the selected day when no
        // specific moments are selected. Clone the array with .slice()
        let momentQuery = this.filterMoments.slice()
        if (!momentQuery.length && this.filterMoments) {
          momentQuery.push(this.filterDay)
        }

        try {
          const response = await axios.get(window.siteUrl.replace(/\/$/, '') + "/api/v1/courses", {
            params: {
              q: this.search,
              page: this.filterPage,
              disciplines: disciplineQuery.join(","),
              ages: this.filterAges.join(","),
              moments: momentQuery.join(","),
              languages: this.filterLanguages.join(","),
            },
          })

          this.items = response.data.entries
          this.filters = response.data.filters
          this.total = response.data.pagination.total
          this.totalPages = response.data.pagination.total_pages
        } catch (e) {
          this.total = 0;
          this.loadingError = true;
        } finally {
          this.loading = false
          this.$nextTick(() => this.$root.lazyLoad.update())
        }
      }, 200);
    },

    formatTeachers(items) {
      return items.length <= 2
        ? items.map((item) => item.title).join(", ")
        : this.$t('courses_overview.various_teachers')
    },

    openFilters() {
      this.modalActive = true

      // https://github.com/medialize/ally.js/issues/139
      setTimeout(() => {
        this.disabledHandle = allyMaintainDisabled({
          filter: [this.$refs.mobileFilters],
        })

        this.keyHandle = allyWhenKey({
          escape: () => this.closeFilters(),
        })
      })
    },

    closeFilters() {
      this.modalActive = false
      this.disabledHandle ? this.disabledHandle.disengage() : null
      this.keyHandle ? this.keyHandle.disengage() : null
    },

    toggleFilters() {
      this.modalActive ? this.closeFilters() : this.openFilters()
    },

    onElementVisibility(value) {
      if (this.loading) {
        return
      }

      this.coursesInview = value
    },
  },
}
</script>
